'use client';

import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ProviderLogo } from './ProviderLogo';
import { getAuthModeLabel } from '@/lib/integrations/utils';
import type { ProviderResponse } from '@/lib/integrations/types';
import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';

interface ProviderCardProps {
  provider: ProviderResponse;
  connectionCount?: number;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}

export function ProviderCard({
  provider,
  connectionCount,
  onClick,
  disabled = false,
  className,
}: ProviderCardProps) {
  const displayName = provider.display_name || provider.provider;
  const authMode = getAuthModeLabel(provider.auth_mode);
  const category = provider.categories?.[0];

  return (
    <Card
      className={cn(
        'cursor-pointer transition-all hover:shadow-md hover:border-primary/50 group relative',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      onClick={disabled ? undefined : onClick}
      title={`${displayName}${category ? ` • ${category}` : ''} • ${authMode}`}
    >
      {/* Connected badge */}
      {connectionCount && connectionCount > 0 && (
        <Badge
          variant="default"
          className="absolute -top-2 -right-2 text-[10px] px-1.5 py-0.5 flex items-center gap-0.5"
        >
          <Check className="size-3" />
          {connectionCount}
        </Badge>
      )}
      <CardContent className="flex flex-col items-center text-center p-4">
        {/* Logo */}
        <div className="mb-3 group-hover:scale-105 transition-transform">
          <ProviderLogo
            provider={provider.provider}
            displayName={provider.display_name}
            logoUrl={provider.logo}
            size="lg"
          />
        </div>

        {/* Provider name */}
        <h3 className="font-medium text-sm leading-tight line-clamp-2 min-h-[2.5rem]">
          {displayName}
        </h3>

        {/* Category & Auth mode */}
        <div className="flex flex-wrap items-center justify-center gap-1 mt-2">
          {category && (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 capitalize">
              {category}
            </Badge>
          )}
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
            {authMode}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
}
